// ERM-5959 needs this utility to sort entity type tasks
// show requested documents first, then required documents alphabetically, then all other tasks
/**
 * Sorts entity type documents by task type priority and task title.
 *
 * Sorting rules:
 * 1. "document_type_requested" tasks appear first (priority 1)
 * 2. "document_type_required" tasks appear second (priority 2)
 * 3. All other task types appear last (priority 3)
 * 4. Within "document_type_required" tasks, items are sorted alphabetically by task_title
 *
 * @param data - Array of objects with task_type and either task_title or title
 * @returns A new sorted array of the same type
 */
export function sortEntityTypeDocuments<
  T extends {
    task_type?: string | null;
    task_title?: string | null;
    title?: string | null;
  },
>(data: T[]): T[] {
  // Return empty array if input is empty
  if (data.length === 0) return [];
  return [...data].sort((a, b) => {
    const typeA = a.task_type ?? '';
    const typeB = b.task_type ?? '';

    // Define priority: requested = 1, required = 2, others = 3
    const getPriority = (type: string): number => {
      if (type === 'document_type_requested' || type === 'document') return 1;
      if (type === 'document_type_required') return 2;
      return 3;
    };

    const priorityA = getPriority(typeA);
    const priorityB = getPriority(typeB);

    // Sort by priority first
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If both are required, sort alphabetically by document type name
    if (priorityA === 2 && priorityB === 2) {
      const docTypeA =
        ('task_title' in a ? a.task_title : a.title)?.toLowerCase() || '';
      const docTypeB =
        ('task_title' in b ? b.task_title : b.title)?.toLowerCase() || '';
      return docTypeA.localeCompare(docTypeB);
    }

    return 0;
  });
}
